import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["map", "tiles", "latitude", "longitude", "status"]

  static values = {
    latitude: Number,
    longitude: Number,
    defaultLatitude: Number,
    defaultLongitude: Number,
    zoom: { type: Number, default: 15 },
    readOnly: { type: Boolean, default: false }
  }

  connect() {
    this.tileSize = 256
    this.resizeHandler = () => this.render()
    window.addEventListener("resize", this.resizeHandler)
    this.render()
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
  }

  select(event) {
    if (this.readOnlyValue) return
    if (event.target.closest("a")) return

    const rect = this.mapTarget.getBoundingClientRect()
    const center = this.currentPoint()
    const x = center.x + (event.clientX - rect.left) - (rect.width / 2)
    const y = center.y + (event.clientY - rect.top) - (rect.height / 2)
    const coords = this.pointToLatLng(x, y)

    this.setCoordinates(coords.lat, coords.lng)
  }

  syncFromInputs() {
    const lat = this.parseCoordinate(this.latitudeTarget.value)
    const lng = this.parseCoordinate(this.longitudeTarget.value)

    if (lat === null || lng === null) {
      this.setStatus("Informe latitude e longitude.")
      return
    }

    if (!this.validLatitude(lat) || !this.validLongitude(lng)) {
      this.setStatus("Coordenadas fora do intervalo.")
      return
    }

    this.latitudeValue = lat
    this.longitudeValue = lng
    this.render()
  }

  render() {
    if (!this.hasMapTarget || !this.hasTilesTarget) return

    const lat = this.safeLatitude()
    const lng = this.safeLongitude()
    const rect = this.mapTarget.getBoundingClientRect()
    const width = rect.width || this.mapTarget.clientWidth || 640
    const height = rect.height || this.mapTarget.clientHeight || 260
    const center = this.latLngToPoint(lat, lng)
    const startX = center.x - (width / 2)
    const startY = center.y - (height / 2)
    const firstTileX = Math.floor(startX / this.tileSize)
    const firstTileY = Math.floor(startY / this.tileSize)
    const lastTileX = Math.floor((startX + width) / this.tileSize)
    const lastTileY = Math.floor((startY + height) / this.tileSize)
    const maxTile = 2 ** this.zoomValue
    const tiles = []

    for (let x = firstTileX; x <= lastTileX; x += 1) {
      for (let y = firstTileY; y <= lastTileY; y += 1) {
        if (y < 0 || y >= maxTile) continue

        const wrappedX = ((x % maxTile) + maxTile) % maxTile
        const left = Math.round((x * this.tileSize) - startX)
        const top = Math.round((y * this.tileSize) - startY)

        tiles.push(this.tileHtml(wrappedX, y, left, top))
      }
    }

    this.tilesTarget.innerHTML = tiles.join("")
    this.setStatus(`${this.format(lat)}, ${this.format(lng)}`)
  }

  tileHtml(x, y, left, top) {
    const src = `https://tile.openstreetmap.org/${this.zoomValue}/${x}/${y}.png`

    return `<img src="${src}" alt="" draggable="false" loading="lazy" style="position:absolute;width:${this.tileSize}px;height:${this.tileSize}px;left:${left}px;top:${top}px;user-select:none;">`
  }

  setCoordinates(lat, lng) {
    const normalizedLat = this.clamp(lat, -90, 90)
    const normalizedLng = this.normalizeLongitude(lng)

    this.latitudeValue = normalizedLat
    this.longitudeValue = normalizedLng
    this.latitudeTarget.value = this.format(normalizedLat)
    this.longitudeTarget.value = this.format(normalizedLng)
    this.dispatchChange(this.latitudeTarget)
    this.dispatchChange(this.longitudeTarget)
    this.render()
  }

  currentPoint() {
    return this.latLngToPoint(this.safeLatitude(), this.safeLongitude())
  }

  latLngToPoint(lat, lng) {
    const sin = Math.sin((lat * Math.PI) / 180)
    const scale = this.tileSize * (2 ** this.zoomValue)

    return {
      x: ((lng + 180) / 360) * scale,
      y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale
    }
  }

  pointToLatLng(x, y) {
    const scale = this.tileSize * (2 ** this.zoomValue)
    const lng = (x / scale) * 360 - 180
    const n = Math.PI - (2 * Math.PI * y) / scale
    const lat = (180 / Math.PI) * Math.atan(Math.sinh(n))

    return { lat, lng }
  }

  safeLatitude() {
    const lat = this.hasLatitudeTarget ? this.parseCoordinate(this.latitudeTarget.value) : null
    if (lat !== null && this.validLatitude(lat)) return lat
    if (this.hasLatitudeValue && this.validLatitude(this.latitudeValue)) return this.latitudeValue
    return this.defaultLatitudeValue
  }

  safeLongitude() {
    const lng = this.hasLongitudeTarget ? this.parseCoordinate(this.longitudeTarget.value) : null
    if (lng !== null && this.validLongitude(lng)) return lng
    if (this.hasLongitudeValue && this.validLongitude(this.longitudeValue)) return this.longitudeValue
    return this.defaultLongitudeValue
  }

  parseCoordinate(value) {
    if (value === null || value === undefined || value === "") return null

    const number = Number(String(value).replace(",", "."))
    return Number.isFinite(number) ? number : null
  }

  validLatitude(value) {
    return value >= -90 && value <= 90
  }

  validLongitude(value) {
    return value >= -180 && value <= 180
  }

  normalizeLongitude(value) {
    if (value < -180 || value > 180) {
      return ((((value + 180) % 360) + 360) % 360) - 180
    }

    return value
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
  }

  format(value) {
    return Number(value).toFixed(6)
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  dispatchChange(element) {
    element.dispatchEvent(new Event("input", { bubbles: true }))
    element.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
